/**
 * Pure identity and Firestore document-key utilities for the Rhythmé catalog.
 *
 * CommonJS source of truth so the trusted backend can reuse the same
 * generators without duplicating them. The client re-exports this module
 * from ids.js. Functions resolve it from the repo locally and copy it
 * into the Functions package on deploy.
 *
 * Canonical entity IDs use cryptographically secure random bytes. Provider,
 * playlist, occurrence, external-ID, and relation keys are deterministic.
 * Do not use the enrichment ULID generator for canonical entities.
 */
/* global globalThis */
'use strict';

const CROCKFORD_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
const SAFE_DIRECT_ID = /^[A-Za-z0-9_.~-]{1,180}$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

const ENTITY_PREFIXES = Object.freeze({
  artist: 'rhy_art_',
  work: 'rhy_wrk_',
  recording: 'rhy_rec_',
  release: 'rhy_rls_',
  mix: 'rhy_mix_',
});

const PROVIDER_KEYS = Object.freeze({
  youtube: 'yt',
  spotify: 'sp',
  apple_music: 'am',
});

const EXTERNAL_NAMESPACES = new Set([
  'mb_artist',
  'mb_work',
  'mb_recording',
  'mb_release',
  'mb_release_group',
  'wikidata',
  'isrc',
  'iswc',
  'acoustid',
  'yt_video',
  'yt_channel',
  'yt_playlist',
  'spotify_track',
  'spotify_artist',
  'apple_music_song',
]);

const WIKIDATA_QID = /^Q[1-9][0-9]*$/;
const CROCKFORD_26 = /^[0-9A-HJKMNP-TV-Z]{26}$/;

const MBID_NAMESPACES = new Set([
  'mb_artist',
  'mb_work',
  'mb_recording',
  'mb_release',
  'mb_release_group',
]);

const PROVIDER_EXTERNAL_NAMESPACES = new Set([
  'yt_video',
  'yt_channel',
  'yt_playlist',
  'spotify_track',
  'spotify_artist',
  'apple_music_song',
]);

const SHA256_CONSTANTS = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5,
  0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
  0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc,
  0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7,
  0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
  0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3,
  0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5,
  0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
  0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

function requireNonEmptyString(value, name) {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new TypeError(`${name} must be a nonempty string`);
  }
  return value;
}

function providerKey(provider) {
  const value = requireNonEmptyString(provider, 'provider');
  const key = PROVIDER_KEYS[value];
  if (!key) {
    throw new RangeError(`Unsupported provider: ${value}`);
  }
  return key;
}

function secureRandomBytes(length) {
  const cryptoObject =
    typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;

  if (!cryptoObject?.getRandomValues) {
    throw new Error('Cryptographically secure randomness is unavailable');
  }

  const bytes = new Uint8Array(length);
  cryptoObject.getRandomValues(bytes);
  return bytes;
}

function utf8Bytes(value) {
  const bytes = [];

  for (const character of value) {
    const codePoint = character.codePointAt(0);
    if (codePoint <= 0x7f) {
      bytes.push(codePoint);
    } else if (codePoint <= 0x7ff) {
      bytes.push(
        0xc0 | (codePoint >>> 6),
        0x80 | (codePoint & 0x3f),
      );
    } else if (codePoint <= 0xffff) {
      bytes.push(
        0xe0 | (codePoint >>> 12),
        0x80 | ((codePoint >>> 6) & 0x3f),
        0x80 | (codePoint & 0x3f),
      );
    } else {
      bytes.push(
        0xf0 | (codePoint >>> 18),
        0x80 | ((codePoint >>> 12) & 0x3f),
        0x80 | ((codePoint >>> 6) & 0x3f),
        0x80 | (codePoint & 0x3f),
      );
    }
  }

  return Uint8Array.from(bytes);
}

function paddedMessage(bytes) {
  const paddedLength = Math.ceil((bytes.length + 9) / 64) * 64;
  const padded = new Uint8Array(paddedLength);
  padded.set(bytes);
  padded[bytes.length] = 0x80;

  const view = new DataView(padded.buffer);
  const bitLength = bytes.length * 8;
  view.setUint32(paddedLength - 8, Math.floor(bitLength / 0x100000000));
  view.setUint32(paddedLength - 4, bitLength >>> 0);
  return { padded, view };
}

function rotateRight(value, count) {
  return (value >>> count) | (value << (32 - count));
}

function rotateLeft(value, count) {
  return (value << count) | (value >>> (32 - count));
}

function sha256(value) {
  const { padded, view } = paddedMessage(utf8Bytes(value));
  const hash = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a,
    0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ]);
  const words = new Uint32Array(64);

  for (let offset = 0; offset < padded.length; offset += 64) {
    for (let index = 0; index < 16; index += 1) {
      words[index] = view.getUint32(offset + index * 4);
    }
    for (let index = 16; index < 64; index += 1) {
      const s0 =
        rotateRight(words[index - 15], 7) ^
        rotateRight(words[index - 15], 18) ^
        (words[index - 15] >>> 3);
      const s1 =
        rotateRight(words[index - 2], 17) ^
        rotateRight(words[index - 2], 19) ^
        (words[index - 2] >>> 10);
      words[index] =
        (words[index - 16] + s0 + words[index - 7] + s1) >>> 0;
    }

    let [a, b, c, d, e, f, g, h] = hash;
    for (let index = 0; index < 64; index += 1) {
      const sum1 = rotateRight(e, 6) ^ rotateRight(e, 11) ^ rotateRight(e, 25);
      const choice = (e & f) ^ (~e & g);
      const temporary1 =
        (h + sum1 + choice + SHA256_CONSTANTS[index] + words[index]) >>> 0;
      const sum0 = rotateRight(a, 2) ^ rotateRight(a, 13) ^ rotateRight(a, 22);
      const majority = (a & b) ^ (a & c) ^ (b & c);
      const temporary2 = (sum0 + majority) >>> 0;

      h = g;
      g = f;
      f = e;
      e = (d + temporary1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (temporary1 + temporary2) >>> 0;
    }

    hash[0] = (hash[0] + a) >>> 0;
    hash[1] = (hash[1] + b) >>> 0;
    hash[2] = (hash[2] + c) >>> 0;
    hash[3] = (hash[3] + d) >>> 0;
    hash[4] = (hash[4] + e) >>> 0;
    hash[5] = (hash[5] + f) >>> 0;
    hash[6] = (hash[6] + g) >>> 0;
    hash[7] = (hash[7] + h) >>> 0;
  }

  const output = new Uint8Array(32);
  const outputView = new DataView(output.buffer);
  hash.forEach((word, index) => outputView.setUint32(index * 4, word));
  return output;
}

function sha1(value) {
  const { padded, view } = paddedMessage(utf8Bytes(value));
  const hash = new Uint32Array([
    0x67452301,
    0xefcdab89,
    0x98badcfe,
    0x10325476,
    0xc3d2e1f0,
  ]);
  const words = new Uint32Array(80);

  for (let offset = 0; offset < padded.length; offset += 64) {
    for (let index = 0; index < 16; index += 1) {
      words[index] = view.getUint32(offset + index * 4);
    }
    for (let index = 16; index < 80; index += 1) {
      words[index] = rotateLeft(
        words[index - 3] ^
          words[index - 8] ^
          words[index - 14] ^
          words[index - 16],
        1,
      );
    }

    let [a, b, c, d, e] = hash;
    for (let index = 0; index < 80; index += 1) {
      let fn;
      let constant;
      if (index < 20) {
        fn = (b & c) | (~b & d);
        constant = 0x5a827999;
      } else if (index < 40) {
        fn = b ^ c ^ d;
        constant = 0x6ed9eba1;
      } else if (index < 60) {
        fn = (b & c) | (b & d) | (c & d);
        constant = 0x8f1bbcdc;
      } else {
        fn = b ^ c ^ d;
        constant = 0xca62c1d6;
      }

      const temporary =
        (rotateLeft(a, 5) + fn + e + constant + words[index]) >>> 0;
      e = d;
      d = c;
      c = rotateLeft(b, 30) >>> 0;
      b = a;
      a = temporary;
    }

    hash[0] = (hash[0] + a) >>> 0;
    hash[1] = (hash[1] + b) >>> 0;
    hash[2] = (hash[2] + c) >>> 0;
    hash[3] = (hash[3] + d) >>> 0;
    hash[4] = (hash[4] + e) >>> 0;
  }

  return Array.from(hash, (word) => word.toString(16).padStart(8, '0')).join('');
}

function crockfordBase32(bytes) {
  let output = '';
  let buffer = 0;
  // Crockford encodes an integer, so a non-multiple-of-five bit length is
  // padded on the left. For 128 bits this yields 26 characters whose first
  // character is 0–7, rather than padding two zero bits on the right.
  let bits = Math.ceil((bytes.length * 8) / 5) * 5 - bytes.length * 8;

  for (const byte of bytes) {
    buffer = (buffer << 8) | byte;
    bits += 8;

    while (bits >= 5) {
      bits -= 5;
      output += CROCKFORD_ALPHABET[(buffer >>> bits) & 31];
    }

    buffer &= (1 << bits) - 1;
  }

  return output;
}

function encodeDocumentIdSegment(value, name) {
  const raw = requireNonEmptyString(value, name);
  if (SAFE_DIRECT_ID.test(raw) && raw !== '.' && raw !== '..') {
    return raw;
  }

  return `b32.${crockfordBase32(sha256(raw).slice(0, 20))}`;
}

function normalizeExternalValue(namespace, value) {
  const raw = requireNonEmptyString(value, 'external value');

  if (namespace === 'wikidata') {
    const normalized = raw.trim().toUpperCase();
    if (!WIKIDATA_QID.test(normalized)) {
      throw new TypeError('external value must be a canonical Wikidata QID');
    }
    return normalized;
  }

  if (namespace === 'isrc') {
    const normalized = raw.toUpperCase().replace(/[\s-]/g, '');
    if (!/^[A-Z]{2}[A-Z0-9]{3}[0-9]{7}$/.test(normalized)) {
      throw new TypeError('external value must be a valid ISRC');
    }
    return normalized;
  }

  if (namespace === 'iswc') {
    const normalized = raw.trim().toUpperCase();
    if (!/^T-[0-9]{3}\.[0-9]{3}\.[0-9]{3}-[0-9]$/.test(normalized)) {
      throw new TypeError('external value must be a canonical ISWC');
    }
    return normalized;
  }

  if (MBID_NAMESPACES.has(namespace) || namespace === 'acoustid') {
    const normalized = raw.trim().toLowerCase();
    if (!UUID.test(normalized)) {
      throw new TypeError(`external value must be a canonical UUID for ${namespace}`);
    }
    return normalized;
  }

  if (PROVIDER_EXTERNAL_NAMESPACES.has(namespace)) {
    return raw;
  }

  throw new RangeError(`Unsupported external namespace: ${namespace}`);
}

function generateCanonicalId(entityType, options = {}) {
  const type = requireNonEmptyString(entityType, 'entity type');
  const prefix = ENTITY_PREFIXES[type];
  if (!prefix) {
    throw new RangeError(`Unsupported canonical entity type: ${type}`);
  }

  const bytes = typeof options.randomBytes === 'function'
    ? options.randomBytes(16)
    : secureRandomBytes(16);
  if (!(bytes instanceof Uint8Array) || bytes.length !== 16) {
    throw new TypeError('canonical ID entropy must be 16 random bytes');
  }

  return `${prefix}${crockfordBase32(bytes)}`;
}

function isCanonicalEntityId(entityType, value) {
  const prefix = ENTITY_PREFIXES[entityType];
  return Boolean(
    prefix
    && typeof value === 'string'
    && value.startsWith(prefix)
    && CROCKFORD_26.test(value.slice(prefix.length)),
  );
}

function generateReleaseTrackId(options = {}) {
  const bytes = typeof options.randomBytes === 'function'
    ? options.randomBytes(16)
    : secureRandomBytes(16);
  if (!(bytes instanceof Uint8Array) || bytes.length !== 16) {
    throw new TypeError('release-track ID entropy must be 16 random bytes');
  }
  return `rhy_rtk_${crockfordBase32(bytes)}`;
}

function providerAssetKey(provider, providerAssetId) {
  return `${providerKey(provider)}:${encodeDocumentIdSegment(
    providerAssetId,
    'providerAssetId',
  )}`;
}

function externalIdKey(namespace, value) {
  const key = requireNonEmptyString(namespace, 'external namespace');
  if (!EXTERNAL_NAMESPACES.has(key)) {
    throw new RangeError(`Unsupported external namespace: ${key}`);
  }

  const normalized = normalizeExternalValue(key, value);
  return `${key}:${encodeDocumentIdSegment(normalized, 'external value')}`;
}

function userPlaylistId(provider, providerPlaylistId) {
  return `${providerKey(provider)}:${encodeDocumentIdSegment(
    providerPlaylistId,
    'providerPlaylistId',
  )}`;
}

function generateNativePlaylistId() {
  return `rhy_pl_${crockfordBase32(secureRandomBytes(16))}`;
}

function userPlaylistItemId(provider, providerOccurrenceId) {
  return `${providerKey(provider)}:${encodeDocumentIdSegment(
    providerOccurrenceId,
    'providerOccurrenceId',
  )}`;
}

function createUserPlaylistItemIdentity({
  provider,
  providerPlaylistItemId,
  providerOccurrenceId,
} = {}) {
  providerKey(provider);

  if (providerPlaylistItemId !== undefined && providerPlaylistItemId !== null) {
    return {
      userPlaylistItemId: userPlaylistItemId(provider, providerPlaylistItemId),
      occurrenceIdSource: 'provider_playlist_item_id',
    };
  }

  if (providerOccurrenceId !== undefined && providerOccurrenceId !== null) {
    return {
      userPlaylistItemId: userPlaylistItemId(provider, providerOccurrenceId),
      occurrenceIdSource: 'provider_occurrence_id',
    };
  }

  return {
    userPlaylistItemId: `rhy_occ_${crockfordBase32(secureRandomBytes(16))}`,
    occurrenceIdSource: 'rhythme_random',
  };
}

function recordingRelationId(fromRecordingId, relation, toRecordingId) {
  const from = requireNonEmptyString(fromRecordingId, 'fromRecordingId');
  const type = requireNonEmptyString(relation, 'relation');
  const to = requireNonEmptyString(toRecordingId, 'toRecordingId');
  return `rel_${sha1(`${from}|${type}|${to}`)}`;
}

function catalogReviewTaskId(providerAssetKey, reason, resolutionVersion) {
  const key = requireNonEmptyString(providerAssetKey, 'providerAssetKey');
  const why = requireNonEmptyString(reason, 'reason');
  const version = Number(resolutionVersion);
  if (!Number.isSafeInteger(version) || version < 0) {
    throw new TypeError('resolutionVersion must be a nonnegative integer');
  }
  return `crt_${crockfordBase32(sha256(`${key}|${why}|${version}`).slice(0, 16))}`;
}

module.exports = {
  CROCKFORD_ALPHABET,
  ENTITY_PREFIXES,
  EXTERNAL_NAMESPACES,
  generateCanonicalId,
  isCanonicalEntityId,
  generateReleaseTrackId,
  providerAssetKey,
  normalizeExternalValue,
  externalIdKey,
  userPlaylistId,
  generateNativePlaylistId,
  userPlaylistItemId,
  createUserPlaylistItemIdentity,
  recordingRelationId,
  catalogReviewTaskId,
};
